import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  makeStyles,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  InputLabel,
} from "@material-ui/core";
import Card from "../../../../components/Card/Card.js";
import CardBody from "../../../../components/Card/CardBody.js";
import { Map as MapIcon, List as ListIcon } from "@material-ui/icons";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import { useTranslation } from "react-i18next";
import { getFilterOptions } from "../../viewServices/calls/apiCalls.js";
import { toast } from "react-toastify";
import AuthContext from "../../../../connections/auth/AuthProvider.js";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import PropTypes from "prop-types";
import { FilterSystem } from "../../sharedComponents/FilterSystem/index.js";

import ChainVisualization from "./ChainVisualization/index.js";
import { CardVisualMap } from "./MapCard/index.js";
import { ScenarioSelector } from "../../sharedComponents/ScenarioSelector";

const overviewVisualViewStyle = (theme, clientColor) => ({
  ...customSelectStyle(theme, clientColor),
  container: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  controlCard: {
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0),
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    padding: theme.spacing(0),
  },
  controlCardBody: {
    padding: 0,
  },
  controlCardContent: {
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  scenarioSelectorContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    zIndex: 3,
    backgroundColor: "rgb(255, 255, 255)",
    padding: "12px 16px",
    borderRadius: "4px",
    width: "90%",
    maxWidth: 280,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  selectorLabel: {
    color: "#888",
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: theme.spacing(1.5),
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flex: "1",
    maxWidth: "calc(100% - 200px)",
    overflow: "visible",
  },
  addScenarioBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginLeft: theme.spacing(2),
    flex: "0 0 auto",
  },
  selectFormControl: {
    marginBottom: theme.spacing(1),
    minWidth: 120,
    maxWidth: 200,
    margin: "7px 0 7px 0 !important",
    "& .MuiInputLabel-root": {
      "&.Mui-focused": {
        color: clientColor + "!important",
      },
    },
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: grayColor[4] + "!important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
  },
  filtersBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flex: "1",
    overflow: "visible",
  },
  divider: {
    width: "1px",
    backgroundColor: grayColor[4],
    alignSelf: "stretch",
    margin: `${theme.spacing(1)}px 0`,
  },
  rightSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexShrink: 0,
  },
  contentCard: {
    flex: 1,
    minWidth: "400px",
    height: "100%",
    maxHeight: "100%",
    display: "flex",
    flexDirection: "column",
    marginTop: theme.spacing(0),
    overflow: "hidden",
    position: "relative",
  },
  contentCardBody: {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
    height: "100%",
    padding: "56px 16px 16px 16px",
    position: "relative",
  },
  selectedScenarioBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    padding: theme.spacing(1, 2),
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  scenarioBarLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  selectedScenarioText: {
    fontSize: "14px",
    fontWeight: "500",
    color: grayColor[1],
  },
  scenarioSelectorInBar: {
    minWidth: "200px",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: grayColor[4] + "!important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
  },
  viewSwitcher: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  iconButton: {
    padding: theme.spacing(0.75),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
  },
  selectedIcon: {
    padding: theme.spacing(0.75),
    border: `1px solid ${clientColor}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: `${clientColor}15`,
    color: clientColor,
    "&:hover": {
      backgroundColor: `${clientColor}25`,
      color: clientColor,
    },
  },
  chainContainer: {
    flex: 2,
    minWidth: "400px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
  },
  mapContainer: {
    flex: 1,
    minWidth: "300px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
  },
});

BoardVisualView.propTypes = {
  scenarios: PropTypes.array.isRequired,
  folders: PropTypes.array,
  isLoading: PropTypes.bool,
  renderViewButtons: PropTypes.func,
  externalSelectedScenarioId: PropTypes.number,
  onExternalScenarioHandled: PropTypes.func,
};

export default function BoardVisualView({
  scenarios,
  folders = [],
  isLoading = false,
  renderViewButtons,
  externalSelectedScenarioId,
  onExternalScenarioHandled,
}) {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;
  const useStyles = makeStyles((theme) =>
    overviewVisualViewStyle(theme, clientColor),
  );
  const classes = useStyles();

  const T_PREFIX = "rgm.cockpit";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  // Independent state for this sub-view
  const [selectedScenario, setSelectedScenario] = useState("");

  // Handle external scenario selection (e.g., from "Open in Scenario Board" button)
  useEffect(() => {
    if (externalSelectedScenarioId) {
      setSelectedScenario(externalSelectedScenarioId);
      if (onExternalScenarioHandled) {
        onExternalScenarioHandled();
      }
    }
  }, [externalSelectedScenarioId, onExternalScenarioHandled]);

  // STANDARD 5 filter dimensions
  const [selectedFilters, setSelectedFilters] = useState({
    products: [],
    brands: [],
    channels: [],
    regions: [],
    clients: [],
  });
  const [filterOptions, setFilterOptions] = useState({
    products: [],
    brands: [],
    channels: [],
    regions: [],
    clients: [],
  });

  // Special parameter - separate from FilterSystem
  const [selectedSolverStatus, setSelectedSolverStatus] = useState("");
  const [solverStatusOptions, setSolverStatusOptions] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [selectedVolume, setSelectedVolume] = useState(
    "input_sellthrough_volume_sales",
  );
  const [availableRoutes, setAvailableRoutes] = useState([]);
  const [selectedRoutes, setSelectedRoutes] = useState("");
  const [mapView, setMapView] = useState("map");

  // Cache and tracking refs
  const cachedFilterOptions = useRef({});
  const previousScenario = useRef(null);

  const VOLUME_WEIGHT_OPTIONS = [
    {
      value: "input_sellthrough_volume_sales",
      label: t("overview.volume_options.volume_sellthrough"),
    },
    {
      value: "input_sellin_volume_sales",
      label: t("overview.volume_options.volume_sellin"),
    },
    { value: "1", label: t("overview.volume_options.not_weighted") },
  ];

  // STANDARD 5 filter dimensions only
  const filterDimensions = [
    { key: "products", label: t("overview.filter_dimensions.products") },
    { key: "brands", label: t("overview.filter_dimensions.brands") },
    { key: "channels", label: t("overview.filter_dimensions.channels") },
    { key: "regions", label: t("overview.filter_dimensions.regions") },
    { key: "clients", label: t("overview.filter_dimensions.clients") },
  ];

  // Load filter options when scenario changes
  useEffect(() => {
    const fetchFilterOptions = async () => {
      if (!selectedScenario) {
        setFilterOptions({
          products: [],
          brands: [],
          channels: [],
          regions: [],
          clients: [],
        });
        setSolverStatusOptions([]);
        return;
      }

      setFiltersLoading(true);
      try {
        const cacheKey = selectedScenario;
        let filterData;

        if (cachedFilterOptions.current[cacheKey]) {
          filterData = cachedFilterOptions.current[cacheKey];
        } else {
          filterData = await getFilterOptions(selectedScenario);
          cachedFilterOptions.current[cacheKey] = filterData;
        }

        // Standard 5 filters
        setFilterOptions({
          products: filterData.products || [],
          brands: filterData.brands || [],
          channels: filterData.channels || [],
          regions: filterData.regions || [],
          clients: filterData.clients || [],
        });
        // Special parameters
        setSolverStatusOptions(filterData.solver_status || []);
        setAvailableRoutes(
          (filterData.considered_routes || []).filter(
            (route) => route && route.trim() !== "",
          ),
        );

        // Reset filters only if scenario actually changed
        if (
          previousScenario.current !== selectedScenario &&
          previousScenario.current !== null
        ) {
          setSelectedFilters({
            products: [],
            brands: [],
            channels: [],
            regions: [],
            clients: [],
          });
          setSelectedSolverStatus("");
          setSelectedRoutes("");
        }

        previousScenario.current = selectedScenario;
      } catch (err) {
        console.error("Failed to fetch filter options:", err);
        toast.error(t("toast.filter_options_error"));
      } finally {
        setFiltersLoading(false);
      }
    };

    fetchFilterOptions();
  }, [selectedScenario, t]);

  const handleFiltersChange = useCallback((newFilters) => {
    setSelectedFilters(newFilters);
  }, []);

  const memoizedAppliedFilters = useMemo(
    () => ({
      ...selectedFilters,
      solver_status: selectedSolverStatus || null,
    }),
    [selectedFilters, selectedSolverStatus],
  );

  return (
    <Box className={classes.container}>
      {/* Control Bar - Volume + Filters + View Buttons (NO scenario selector) */}
      <Card className={classes.controlCard}>
        <CardBody className={classes.controlCardBody}>
          <Box className={classes.controlCardContent}>
            {/* Left Section: Volume + Solver Status + Filters */}
            <Box className={classes.leftSection}>
              {/* Volume Selector */}
              <Box className={classes.addScenarioBox}>
                <FormControl
                  className={classes.selectFormControl}
                  style={{ minWidth: 140, marginBottom: 0 }}
                >
                  <InputLabel id="volume-selector-label">
                    {t("overview.volume_weight_label")}
                  </InputLabel>
                  <Select
                    labelId="volume-selector-label"
                    value={selectedVolume}
                    onChange={(e) => setSelectedVolume(e.target.value)}
                    label={t("overview.volume_weight_label")}
                    disabled={!selectedScenario || filtersLoading}
                    MenuProps={{
                      className: classes.selectMenu,
                      classes: { paper: classes.selectPaper },
                    }}
                  >
                    {VOLUME_WEIGHT_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        className={classes.selectMenuItem}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Solver Status Dropdown */}
              <Box className={classes.addScenarioBox}>
                <FormControl
                  className={classes.selectFormControl}
                  style={{ minWidth: 140, marginBottom: 0 }}
                >
                  <InputLabel id="solver-status-selector-label" shrink>
                    {t("overview.solver_status_label")}
                  </InputLabel>
                  <Select
                    labelId="solver-status-selector-label"
                    value={selectedSolverStatus}
                    onChange={(e) => setSelectedSolverStatus(e.target.value)}
                    label={t("overview.solver_status_label")}
                    displayEmpty
                    disabled={!selectedScenario || filtersLoading}
                    renderValue={(selected) => {
                      if (!selected || selected === "") {
                        return <em>{t("overview.all_statuses")}</em>;
                      }
                      return selected;
                    }}
                    MenuProps={{
                      className: classes.selectMenu,
                      classes: { paper: classes.selectPaper },
                    }}
                  >
                    <MenuItem value="" className={classes.selectMenuItem}>
                      <em>{t("overview.all_statuses")}</em>
                    </MenuItem>
                    {solverStatusOptions.map((option) => (
                      <MenuItem
                        key={option}
                        value={option}
                        className={classes.selectMenuItem}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box className={classes.divider} />

              {/* FilterSystem - Standard 5 dimensions */}
              <Box className={classes.filtersBox}>
                <FilterSystem
                  dimensions={filterDimensions}
                  filterOptions={filterOptions}
                  selectedFilters={selectedFilters}
                  onFiltersChange={handleFiltersChange}
                  clientColor={clientColor}
                  disabled={!selectedScenario || filtersLoading}
                />
              </Box>
            </Box>

            {/* Right Section: View Buttons only */}
            <Box className={classes.rightSection}>
              {renderViewButtons && renderViewButtons()}
            </Box>
          </Box>
        </CardBody>
      </Card>

      {/* Content Card with Scenario Selector Overlay and Scenario Bar */}
      <Card className={classes.contentCard}>
        <CardBody className={classes.contentCardBody}>
          {/* Scenario Selector Overlay - show when no scenario selected */}
          {!selectedScenario && (
            <Box className={classes.scenarioSelectorContainer}>
              <Box className={classes.selectorLabel}>
                {t("map.tooltip.select_scenario")}
              </Box>
              <ScenarioSelector
                scenarios={scenarios}
                folders={folders}
                value={selectedScenario}
                onChange={setSelectedScenario}
                placeholder={t("overview.choose_scenario")}
                clientColor={clientColor}
                isLoading={isLoading}
                fullWidth
              />
            </Box>
          )}

          {/* Selected Scenario Bar - appears when scenario is selected */}
          {selectedScenario && (
            <Box className={classes.selectedScenarioBar}>
              <Box className={classes.scenarioBarLeft}>
                <Box
                  className={classes.selectedScenarioText}
                  style={{ marginRight: "8px" }}
                >
                  {t("overview.select_scenario")}
                </Box>
                <ScenarioSelector
                  scenarios={scenarios}
                  folders={folders}
                  value={selectedScenario}
                  onChange={setSelectedScenario}
                  clientColor={clientColor}
                  isLoading={isLoading}
                />

                <Box className={classes.divider} />

                {/* Considered Routes Label and Selector */}
                <Box
                  className={classes.selectedScenarioText}
                  style={{ marginRight: "8px" }}
                >
                  {t("overview.considered_routes")}:
                </Box>
                <FormControl
                  className={classes.scenarioSelectorInBar}
                  size="small"
                  style={{ minWidth: "140px" }}
                >
                  <Select
                    value={selectedRoutes}
                    onChange={(e) => setSelectedRoutes(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected || selected === "") {
                        return <em>{t("overview.all_routes")}</em>;
                      }
                      return selected;
                    }}
                    MenuProps={{
                      className: classes.selectMenu,
                      classes: { paper: classes.selectPaper },
                    }}
                  >
                    <MenuItem value="" className={classes.selectMenuItem}>
                      <em>{t("overview.all_routes")}</em>
                    </MenuItem>
                    {availableRoutes.map((route) => (
                      <MenuItem
                        key={route}
                        value={route}
                        className={classes.selectMenuItem}
                      >
                        {route}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Map View Switcher on the right */}
              <Box className={classes.viewSwitcher}>
                <Tooltip title={t("overview.geographic_view_tooltip")}>
                  <IconButton
                    onClick={() => setMapView("map")}
                    className={
                      mapView === "map"
                        ? classes.selectedIcon
                        : classes.iconButton
                    }
                    size="small"
                  >
                    <MapIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("overview.top_clients_tooltip")}>
                  <IconButton
                    onClick={() => setMapView("clients")}
                    className={
                      mapView === "clients"
                        ? classes.selectedIcon
                        : classes.iconButton
                    }
                    size="small"
                  >
                    <ListIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          <Box className={classes.chainContainer}>
            <ChainVisualization
              scenarioId={selectedScenario}
              filters={memoizedAppliedFilters}
              isSkeletonMode={!selectedScenario}
              selectedVolume={selectedVolume}
              selectedRoutes={selectedRoutes}
            />
          </Box>
          <Box className={classes.mapContainer}>
            <CardVisualMap
              selectedScenario={selectedScenario}
              appliedFilters={memoizedAppliedFilters}
              currentView={mapView}
              onViewChange={setMapView}
              selectedVolume={selectedVolume}
            />
          </Box>
        </CardBody>
      </Card>
    </Box>
  );
}
